import React, { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMst } from '../../core/stores';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { Form, FormSchema, getElementByName } from '@jht/react-library';
import {
  existingOrganizationFormSchema,
  newOrganizationFormSchema,
} from './organization-form-schemas';
import { OrderBreadcrumbs } from '../../components/OrderBreadcrumbs/OrderBreadcrumbs';
import {
  NewDigitalOrderOrganization,
  ExistingDigitalOrderOrganization,
  NewOrExisting,
  DigitalOrdersBase,
} from '../../models';
import { keyAccounts } from '../../libs/static/keyAccounts';

export interface DOOrganizationProps extends RouteComponentProps<{ id: string; from: string }> {}

const useStyles = makeStyles(theme => ({
  paper: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '88%',
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  formContainer: {
    flexGrow: 1,
    height: '100%',
    margin: theme.spacing(2),
    '& > *': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  buttonGroup: {
    width: '100%',
  },
  button: {
    width: '50%',
  },
  title: {
    width: '100%',
  },
  buttomButtonRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  summary: {
    margin: 0,
  },
}));

export const OrganizationForm: React.FC<DOOrganizationProps> = observer(({ match, location }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const orderId = match.params.id;
  const { digitalOrdersStore, navigationStore } = useMst();
  const order = digitalOrdersStore.order(orderId);
  const [selection, setSelection] = useState(
    (order.organization && order.organization.newOrExistingOrganization) || '',
  );
  const [newOrgForm, setNewOrgForm] = useState<FormSchema | null>(null);

  useEffect(() => {
    setNewOrgForm(
      newOrganizationFormSchema(
        order.organization
          ? (order.organization as NewDigitalOrderOrganization)
          : ({
              newOrExistingOrganization: NewOrExisting.NEW,
              isActive: '1',
              name: '',
            } as NewDigitalOrderOrganization),
        order.organization &&
          order.organization.newOrExistingOrganization !== NewOrExisting.NEW &&
          order.facility &&
          order.facility.facilities.length > 0,
        digitalOrdersStore.existingOrganizationNames,
      ),
    );
  }, [order]);

  const [existingOrgForm, setExistingOrgForm] = useState(
    existingOrganizationFormSchema(
      order.organization
        ? (order.organization as ExistingDigitalOrderOrganization)
        : ({
            organizationId: null,
            newOrExistingOrganization: NewOrExisting.EXISTING,
          } as ExistingDigitalOrderOrganization),
      digitalOrdersStore.organizations,
      order.organization && order.facility && order.facility.facilities.length > 0,
    ),
  );

  const submit = async () => {
    await digitalOrdersStore.submitDigitalOrder(orderId);
    navigationStore.push(`/digital-orders/${orderId}/facilities`);
  };

  const back = () => {
    navigationStore.push(`/digital-orders`);
  };

  const nextDisabled = useMemo(() => {
    if (selection === NewOrExisting.NEW && newOrgForm) {
      return (
        (newOrgForm?.elements as any).some(element => element.required && element.value === '') ||
        !newOrgForm.valid
      );
    } else if (selection === NewOrExisting.EXISTING && existingOrgForm) {
      return (existingOrgForm.elements as any).some(
        element => element.required && (element.value === '' || element.value === null),
      );
    } else {
      return true;
    }
  }, [selection, newOrgForm, existingOrgForm]);

  const handleNewOrgFormChange = formState => {
    const name = getElementByName(formState.elements, 'name').value as string,
      orgUpdate = {
        newOrExistingOrganization: NewOrExisting.NEW,
        isActive: '1',
        name: name,
      },
      clearFacilities = !(
        order.organization && order.organization.newOrExistingOrganization === NewOrExisting.NEW
      );
    formState.elements[0].helperText = null;
    setNewOrgForm(formState);
    writeOrder(orgUpdate, clearFacilities);
  };

  const handleSelectOrgFormChange = formState => {
    const selectedOrgId = getElementByName(formState.elements, 'organization').value as string,
      orgUpdate = selectedOrgId
        ? {
            newOrExistingOrganization: NewOrExisting.EXISTING,
            organizationId: Number(selectedOrgId),
          }
        : null,
      clearFacilities = order.organization ? true : false;

    formState.elements[0].helperText = null;
    setExistingOrgForm(formState);
    if (orgUpdate) {
      writeOrder(orgUpdate, clearFacilities);
    }
  };

  const writeOrder = (orgData: any, clearFacilities: boolean) => {
    if (orgData.organizationId && keyAccounts.includes(orgData.organizationId)) {
      orgData.keyAccount = true;
    }

    digitalOrdersStore.writeToCurrentOrder(
      orderId,
      Object.assign(
        {
          organization: orgData,
        },
        clearFacilities
          ? {
              facility: {
                facilities: [],
              },
            }
          : {},
      ) as Partial<DigitalOrdersBase>,
    );
  };

  return (
    <Paper className={classes.paper}>
      <div className={classes.formContainer}>
        <Typography className={classes.title} variant="h5">
          Select organization
        </Typography>
        <Typography className={classes.title} variant="body1">
          Is this a new or existing organization?
        </Typography>
        <ToggleButtonGroup
          className={classes.buttonGroup}
          exclusive
          value={selection}
          onChange={(evt, newVal) => setSelection(newVal)}
        >
          <ToggleButton className={classes.button} value={NewOrExisting.NEW}>
            New Organization
          </ToggleButton>
          <ToggleButton className={classes.button} value={NewOrExisting.EXISTING}>
            Existing Organization
          </ToggleButton>
        </ToggleButtonGroup>
        {selection === NewOrExisting.NEW && newOrgForm && (
          <Form schema={newOrgForm} onChange={handleNewOrgFormChange} onSubmit={() => {}}>
            <></>
          </Form>
        )}
        {selection === NewOrExisting.EXISTING && (
          <Form schema={existingOrgForm} onChange={handleSelectOrgFormChange} onSubmit={() => {}}>
            <></>
          </Form>
        )}
        <div className={classes.buttomButtonRow}>
          <Button variant="contained" color="secondary" onClick={back}>
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={submit} disabled={nextDisabled}>
            Next
          </Button>
        </div>
      </div>
      <OrderBreadcrumbs className={classes.summary} orderId={orderId} />
    </Paper>
  );
});
